import React, { useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { Group } from '@visx/group';

import { LegendOrdinal } from '@visx/legend';
import { useParentSize } from '@visx/responsive';
import { scaleOrdinal } from '@visx/scale';
import { Pie } from '@visx/shape';

import { Text } from '@visx/text';

import { EmptyBox } from 'shared/base';

import { PieChartTooltipContent } from './tooltipContent';
import {
  CHART_CONTENT_HEIGHT,
  CHART_HOVER_OPACITY,
  CHART_HOVER_TRANSITION,
  NO_RESULTS_FOUND_MESSAGE,
  OTHER_VALUE,
  PIE_CHART_INNER_TEXT_GAP,
  PIE_CHART_INNER_TEXT_Y_OFFSET,
  PIE_CHART_LEGEND_ITEMS_GAP,
  PIE_CHART_RADIUS_DIFFERENCE,
} from '../../constants';
import { PieChartData, PieChartVariant } from '../../types';
import { useBisectPieData, getPiePositionProps } from '../../utils/pieUtils';
import { ChartTooltip } from '../tooltip/tooltip';
import { useChartTooltip } from '../tooltip/useChartTooltip';

interface Props extends Omit<PieChartVariant, 'type'> {
  data: PieChartData;
}

export const PieChart = ({ data, total = { label: 'Total', show: true } }: Props) => {
  const { palette, typography } = useTheme();

  const {
    TooltipInPortal,
    containerRef,
    handleLegendItemMouseOut,
    handleLegendItemMouseOver,
    handleMouseOutSvg,
    handleMouseOverSvg,
    hoveredLegendItem,
    popoverProps,
    tooltipData,
    tooltipProps,
  } = useChartTooltip<PieChartData[number]>({ anchorOn: 'move' });

  const { height, parentRef, width: w } = useParentSize({ debounceTime: 0 });
  const width = React.useMemo(() => w, [w]);

  const pieValue = useCallback((d: PieChartData[number]) => d.value, []);

  const { groupLeftOffset, groupTopOffset, pieChartRadius } = getPiePositionProps(
    width,
    height,
    data,
  );

  const { colorWheel, primaryData, secondaryData, totalValue } = useBisectPieData(data, 10);

  const colorScale = scaleOrdinal({
    domain: primaryData.map((d) => d.name),
    range: colorWheel,
  });

  if (totalValue === 0) {
    return <EmptyBox title={NO_RESULTS_FOUND_MESSAGE} />;
  }

  return (
    <Stack
      ref={parentRef}
      alignItems="center"
      direction="row"
      height={CHART_CONTENT_HEIGHT}
      position="relative"
      spacing={1}
      width="100%"
    >
      <Box width="50%">
        <svg ref={containerRef} height={height} width={width}>
          <Group left={groupLeftOffset} top={groupTopOffset}>
            <Pie
              data={primaryData}
              innerRadius={pieChartRadius - PIE_CHART_RADIUS_DIFFERENCE}
              outerRadius={pieChartRadius}
              pieValue={pieValue}
            >
              {(pie) => {
                return pie.arcs.map((arc) => {
                  const { name } = arc.data;
                  const arcPath = pie.path(arc);
                  const arcFill = colorScale(name);

                  const isArcHighlighted = hoveredLegendItem?.name === name;
                  const arcOpacity = hoveredLegendItem
                    ? isArcHighlighted
                      ? 1
                      : CHART_HOVER_OPACITY
                    : 1;

                  return (
                    <g key={`arc-${name}`}>
                      <path
                        d={arcPath!}
                        fill={arcFill}
                        onMouseMove={(event) => handleMouseOverSvg(event, arc.data)}
                        onMouseOut={handleMouseOutSvg}
                        opacity={arcOpacity}
                        style={{ cursor: 'pointer', transition: CHART_HOVER_TRANSITION }}
                      />
                    </g>
                  );
                });
              }}
            </Pie>

            {total.show !== false && (
              <>
                <Text
                  dy={PIE_CHART_INNER_TEXT_Y_OFFSET}
                  fill={palette.text.secondary}
                  fontSize={typography.caption.fontSize}
                  textAnchor="middle"
                >
                  {tooltipData?.label ?? hoveredLegendItem?.label ?? total.label}
                </Text>
                <Text
                  dy={PIE_CHART_INNER_TEXT_Y_OFFSET + 12 + PIE_CHART_INNER_TEXT_GAP}
                  fill={palette.text.primary}
                  fontSize={typography.h6.fontSize}
                  textAnchor="middle"
                >
                  {new Intl.NumberFormat('en', { notation: 'compact' }).format(
                    tooltipData?.value ?? hoveredLegendItem?.value ?? totalValue ?? 0,
                  )}
                </Text>
              </>
            )}
          </Group>
        </svg>
      </Box>
      <LegendOrdinal direction="column" scale={colorScale} shapeWidth={7}>
        {(labels) => {
          return (
            <Box maxWidth="50%">
              {labels.map((label, i) => {
                const dataItem = primaryData.find((d) => d.name === label.datum);
                if (!dataItem) {
                  return;
                }

                const total = primaryData.reduce((acc, curr) => acc + curr.value, 0);
                const isItemHovered = hoveredLegendItem?.name === dataItem.name;
                const itemOpacity = hoveredLegendItem
                  ? isItemHovered
                    ? 1
                    : CHART_HOVER_OPACITY
                  : 1;

                return (
                  <Stack
                    key={i}
                    alignItems="center"
                    direction="row"
                    justifyContent="space-between"
                    onMouseEnter={() => handleLegendItemMouseOver(dataItem)}
                    onMouseLeave={handleLegendItemMouseOut}
                    style={{
                      cursor: 'pointer',
                      marginTop: i > 0 ? PIE_CHART_LEGEND_ITEMS_GAP : 0,
                      opacity: itemOpacity,
                      transition: CHART_HOVER_TRANSITION,
                      width: '100%',
                    }}
                  >
                    <Typography
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      variant="body2"
                    >
                      <svg
                        height={14}
                        style={{ marginRight: PIE_CHART_LEGEND_ITEMS_GAP }}
                        width={8}
                      >
                        <rect fill={label.value} height={14} rx={2} ry={2} width={7} />
                      </svg>
                      <span>{dataItem.label}</span>
                    </Typography>

                    <Box alignItems="center" display="flex" maxWidth="30%" ml={2}>
                      <Typography color="text.secondary" variant="body2">
                        {((dataItem.value / total) * 100).toFixed(0)}%
                      </Typography>
                    </Box>
                  </Stack>
                );
              })}
            </Box>
          );
        }}
      </LegendOrdinal>
      <ChartTooltip
        popoverProps={popoverProps}
        TooltipInPortal={TooltipInPortal}
        tooltipProps={tooltipProps}
      >
        <PieChartTooltipContent
          colorWheel={colorWheel}
          primaryData={primaryData}
          secondaryData={secondaryData}
          selectedName={tooltipData?.name}
          showSecondary={tooltipData?.name === OTHER_VALUE}
          total={totalValue}
        />
      </ChartTooltip>
    </Stack>
  );
};
